const ALLOWED_TAGS = new Set([
  "p",
  "br",
  "strong",
  "b",
  "em",
  "i",
  "u",
  "ul",
  "ol",
  "li",
  "h3",
  "h4",
  "blockquote",
  "a",
]);

function safeHref(value: string) {
  const trimmed = value.trim();
  if (/^(https?:|mailto:|tel:)/i.test(trimmed) && !/[\s"'<>]/.test(trimmed)) return trimmed;
  return null;
}

/**
 * Whitelist sanitizer for the small set of formatting tags the rich text
 * editor can produce. Runs on both server and client, no DOM required.
 */
export function sanitizeRichText(input: string) {
  if (!input) return "";
  let html = input
    .replace(/<!--[\s\S]*?-->/g, "")
    .replace(/<\s*(script|style|iframe|object|embed)[\s\S]*?<\s*\/\s*\1\s*>/gi, "")
    .replace(/<\s*(script|style|iframe|object|embed)\b[^>]*>/gi, "");

  html = html.replace(/<\s*(\/?)\s*([a-zA-Z0-9]+)([^>]*)>/g, (_match, closing, rawTag, attrs) => {
    const tag = String(rawTag).toLowerCase();
    if (!ALLOWED_TAGS.has(tag)) return "";
    if (closing) return `</${tag}>`;
    if (tag === "a") {
      const hrefMatch = /href\s*=\s*("([^"]*)"|'([^']*)'|([^\s>]+))/i.exec(attrs ?? "");
      const href = safeHref(hrefMatch?.[2] ?? hrefMatch?.[3] ?? hrefMatch?.[4] ?? "");
      if (!href) return "";
      return `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">`;
    }
    return `<${tag}>`;
  });

  // Pressing Enter twice leaves an empty paragraph behind. Paragraphs already
  // carry their own spacing, so those blanks read as a double gap in email
  // clients even though the composer collapses them. Drop them so what gets
  // sent matches what was written.
  html = html.replace(/<p>(?:\s|<br\s*\/?>|&nbsp;)*<\/p>/gi, "");

  return html.trim();
}

export function richTextToPlainText(input: string) {
  if (!input) return "";
  return input
    .replace(/<\s*(br|\/p|\/li|\/h3|\/h4|\/blockquote)\s*\/?>/gi, " ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\s+/g, " ")
    .trim();
}

export function isRichText(input: string) {
  return /<\/?(p|br|ul|ol|li|strong|em|u|h3|h4|a|b|i|blockquote)\b/i.test(input ?? "");
}

/** Turns legacy plain-text descriptions into simple paragraphs. */
export function richTextOrParagraphs(input: string) {
  const value = input ?? "";
  if (!value.trim()) return "";
  if (isRichText(value)) return sanitizeRichText(value);
  return value
    .split(/\n{2,}/)
    .map(
      (block) =>
        `<p>${block
          .replace(/&/g, "&amp;")
          .replace(/</g, "&lt;")
          .replace(/>/g, "&gt;")
          .replace(/\n/g, "<br>")}</p>`,
    )
    .join("");
}

const INLINE_MAP: Record<string, string> = {
  b: "strong",
  strong: "strong",
  i: "em",
  em: "em",
  u: "u",
};

const BLOCK_MAP: Record<string, string> = {
  div: "p",
  p: "p",
  h1: "h3",
  h2: "h3",
  h3: "h3",
  h4: "h4",
  h5: "h4",
  h6: "h4",
  blockquote: "blockquote",
  li: "li",
};

function escapeText(value: string) {
  return value
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;");
}

function stylesToTags(el: Element) {
  const style = (el as HTMLElement).style;
  const tags: string[] = [];
  const weight = style?.fontWeight ?? "";
  if (weight === "bold" || weight === "bolder" || Number(weight) >= 600) tags.push("strong");
  if (style?.fontStyle === "italic") tags.push("em");
  if ((style?.textDecoration || style?.textDecorationLine || "").includes("underline")) tags.push("u");
  return tags;
}

/**
 * Browser-only: converts contentEditable output (styled spans, divs, nested
 * markup) into the small whitelist of tags the sanitizer keeps.
 */
export function normalizeEditorHtml(root: HTMLElement | null): string {
  if (!root) return "";

  function children(node: Node): string {
    let out = "";
    node.childNodes.forEach((child) => {
      out += serialize(child);
    });
    return out;
  }

  function serialize(node: Node): string {
    if (node.nodeType === 3) return escapeText(node.textContent ?? "");
    if (node.nodeType !== 1) return "";
    const el = node as Element;
    const tag = el.tagName.toLowerCase();
    if (tag === "script" || tag === "style") return "";
    if (tag === "br") return "<br>";

    let inner = children(el);
    const wrapWith = (tags: string[], html: string) =>
      tags.reduceRight((acc, t) => `<${t}>${acc}</${t}>`, html);

    if (tag === "a") {
      const href = safeHref(el.getAttribute("href") ?? "");
      if (!href) return inner;
      return `<a href="${href}" target="_blank" rel="noopener noreferrer nofollow">${inner}</a>`;
    }

    if (tag === "ul" || tag === "ol") return `<${tag}>${inner}</${tag}>`;

    const styleTags = stylesToTags(el);
    if (INLINE_MAP[tag]) return wrapWith([INLINE_MAP[tag], ...styleTags], inner);
    if (tag === "span" || tag === "font") return wrapWith(styleTags, inner);

    const block = BLOCK_MAP[tag];
    if (block) {
      inner = wrapWith(styleTags, inner);
      if (!inner.trim()) return block === "li" ? "" : "";
      return `<${block}>${inner}</${block}>`;
    }

    return wrapWith(styleTags, inner);
  }

  return sanitizeRichText(children(root));
}

/** HTML -> plain text that keeps line breaks (for snippets and text/plain parts). */
export function richTextToPlainLines(input: string) {
  if (!input) return "";
  return input
    .replace(/<\s*br\s*\/?>/gi, "\n")
    .replace(/<\/\s*(p|div|h3|h4|li|blockquote)\s*>/gi, "\n")
    .replace(/<\s*li\s*>/gi, "- ")
    .replace(/<[^>]*>/g, "")
    .replace(/&nbsp;/g, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/\n{3,}/g, "\n\n")
    .trim();
}
